import React, { useState, useRef, useEffect } from "react";
import * as echarts from 'echarts'; // 直接引入ECharts核心库
import Texts from '@/Pages/Project/Flow/TaskManagement/TaskDate/textsBase64.js';
import "./hydraulics.less"; // 引入Less样式

// 解码base64的ECharts脚本（如果Texts是base64编码的echarts.js）
const decodeBase64Script = (base64Str: string) => {
  try {
    const decoded = atob(base64Str);
    // 执行ECharts脚本（仅首次执行）
    if (!window.echarts) {
      const script = document.createElement('script');
      script.textContent = decoded;
      document.head.appendChild(script);
    }
  } catch (e) {
    console.error('解码ECharts脚本失败:', e);
  }
};

const HydraulicsChart = (props: any) => {
  const { hyResult, trayNo, unit } = props;
  const chartRef = useRef<HTMLDivElement>(null); // ECharts容器ref
  const [isLoading, setIsLoading] = useState(true); // 加载状态
  const myChart = useRef<echarts.ECharts | null>(null); // ECharts实例

  // 处理单位显示
  const showUnit = () => {
    if (unit === 'm3/s') {
      return 'm³/s';
    } else if (unit === 'm3/h') {
      return 'm³/h';
    } else {
      return unit;
    }
  };

  // 初始化ECharts
  useEffect(() => {
    // 解码并加载ECharts脚本
    if (Texts) {
      decodeBase64Script(Texts);
    }

    // 确保数据完整后初始化图表
    if (!hyResult || !hyResult.result) return;

    // 等待ECharts加载完成
    const initChart = () => {
      if (!chartRef.current) return;

      // 初始化ECharts实例
      myChart.current = echarts.init(chartRef.current);
      setIsLoading(false);

      // 配置图表参数
      const option = getChartOption();
      myChart.current.setOption(option);

      // 监听窗口大小变化，自适应图表
      const resizeHandler = () => {
        myChart.current?.resize();
      };
      window.addEventListener('resize', resizeHandler);

      // 清理函数
      return () => {
        window.removeEventListener('resize', resizeHandler);
        myChart.current?.dispose();
      };
    };

    // 如果ECharts已加载，直接初始化；否则延迟等待
    if (window.echarts) {
      initChart();
    } else {
      const timer = setTimeout(() => {
        initChart();
      }, 500);
      return () => clearTimeout(timer);
    }

  }, [hyResult, trayNo, unit]);

  // 构建ECharts配置项
  const getChartOption = () => {
    if (!hyResult || !hyResult.result) return {};

    // 解构数据
    const { L_values, V_flood, V_leakage, V_max_entrain, L_op, V_op, V_L_ratio_op, L_min, L_max } = hyResult.result;
    
    // 数据准备
    const LUnitValues = L_values[unit];
    const VFloodValues = V_flood[unit];
    const VLeakageValues = V_leakage[unit];
    const VMaxEntrainValues = V_max_entrain[unit];
    const LOpValue = L_op[unit];
    const VOpValue = V_op[unit];
    const ratioValue = V_L_ratio_op[unit];
    const LMinValue = L_min[unit];
    const LMaxValue = L_max[unit];

    // 操作线数据
    const opLineData = [
      [LMinValue, LMinValue * ratioValue],
      [LMaxValue, LMaxValue * ratioValue]
    ];

    // 安全操作区数据
    const safetyZoneData = [];
    // 漏液线（下界）
    for (let i = 0; i < LUnitValues.length; i++) {
      safetyZoneData.push([LUnitValues[i], VLeakageValues[i]]);
    }
    // 液泛线/最大夹带线（上界）
    for (let i = LUnitValues.length - 1; i >= 0; i--) {
      safetyZoneData.push([LUnitValues[i], VFloodValues[i] > VMaxEntrainValues[i] ? VMaxEntrainValues[i] : VFloodValues[i]]);
    }

    // ECharts配置项
    return {
      backgroundColor: 'transparent',
      title: {
        text: `${trayNo + 1}#塔板 水力学操作性能负荷图`,
        top: 0,
        left: 'center',
        textStyle: {
          color: '#000',
          fontSize: 24,
        },
      },
      legend: {
        data: [
          {
            name: '安全操作区',
            icon: 'rect',
            itemStyle: {
              color: 'rgba(16, 185, 129, 0.2)',
              borderColor: 'rgba(16, 185, 129, 1)',
              borderWidth: 1,
            }
          },
          {
            name: '液泛线',
            itemStyle: {
              color: 'transparent',
            },
            lineStyle: {
              width: 4,
            },
          },
          {
            name: '漏液线',
            itemStyle: {
              color: 'transparent',
            },
            lineStyle: {
              width: 4,
            },
          },
          {
            name: '最大夹带线',
            itemStyle: {
              color: 'transparent',
            },
            lineStyle: {
              width: 4,
            },
          },
          {
            name: '操作线',
            itemStyle: {
              color: 'transparent',
            },
            lineStyle: {
              width: 4,
              color: '#60a5fa',
            },
          },
          '操作点',
          {
            name: '液相上限',
            itemStyle: {
              color: 'transparent',
            },
            lineStyle: {
              width: 4,
              color: '#a855f7',
            },
          },
          {
            name: '液相下限',
            itemStyle: {
              color: 'transparent',
            },
            lineStyle: {
              width: 4,
              color: '#ec4899',
            },
          }
        ],
        orient: 'vertical',
        right: 8,
        top: 70,
        itemWidth: 20,
        itemHeight: 10,
        textStyle: { fontSize: 14, fontWeight: 400, color: '#1e293b' },
        backgroundColor: 'rgba(255,255,255,0.9)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        borderRadius: 8,
        padding: [8, 20]
      },
      grid: {
        left: 60,
        right: 160,
        bottom: 80,
        top: 70,
        containLabel: true
      },
      xAxis: {
        type: 'value',
        name: `液相流量 L (${showUnit()})`,
        nameLocation: 'middle',
        nameGap: 35,
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e2e8f0',
            type: 'dashed'
          }
        },
        axisLine: {
          lineStyle: {
            color: '#94a3b8'
          }
        },
        axisLabel: {
          color: '#64748b',
          formatter: function (value: number) {
            return value.toLocaleString();
          }
        },
        nameTextStyle: {
          color: '#475569',
          fontSize: 16,
        }
      },
      yAxis: {
        type: 'value',
        name: `气相流量 V (${showUnit()})`,
        nameLocation: 'middle',
        nameGap: 60,
        splitLine: {
          show: true,
          lineStyle: {
            color: '#e2e8f0',
            type: 'dashed'
          }
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: '#94a3b8'
          }
        },
        axisLabel: {
          color: '#64748b',
          formatter: function (value: number) {
            return value.toLocaleString();
          }
        },
        nameTextStyle: {
          color: '#475569',
          fontSize: 16,
        }
      },
      series: [
        {
          name: '安全操作区',
          type: 'custom',
          renderItem: function (params: any, api: any) {
            if (params.context.rendered) {
              return;
            }
            params.context.rendered = true;

            const points = safetyZoneData.map(item => {
              return api.coord(item);
            });

            return {
              type: 'polygon',
              shape: {
                points: points
              },
              style: {
                fill: 'rgba(16, 185, 129, 0.15)',
              }
            };
          },
          data: safetyZoneData,
          z: 0,
          silent: true
        },
        {
          name: '液泛线',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: LUnitValues.map((l: number, i: number) => [l, VFloodValues[i]]),
          lineStyle: {
            width: 3,
            color: '#f87171'
          },
          z: 3
        },
        {
          name: '漏液线',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: LUnitValues.map((l: number, i: number) => [l, VLeakageValues[i]]),
          lineStyle: {
            width: 3,
            color: '#fb923c'
          },
          z: 3
        },
        {
          name: '最大夹带线',
          type: 'line',
          smooth: true,
          symbol: 'none',
          data: LUnitValues.map((l: number, i: number) => [l, VMaxEntrainValues[i]]),
          lineStyle: {
            width: 3,
            type: 'dotted',
            color: '#322463'
          },
          z: 3
        },
        {
          name: '液相上限',
          type: 'line',
          markLine: {
            symbol: ['none', 'none'],
            label: {
              show: true,
              position: 'end',
              formatter: '液相上限',
              color: '#a855f7',
              fontSize: 12,
              fontWeight: 'bold'
            },
            lineStyle: {
              type: 'dashed',
              color: '#a855f7',
              width: 2
            },
            data: [
              {
                xAxis: LMaxValue,
                name: '液相上限'
              }
            ],
            animation: false
          },
          z: 2
        },
        {
          name: '液相下限',
          type: 'line',
          markLine: {
            symbol: ['none', 'none'],
            label: {
              show: true,
              position: 'end',
              formatter: '液相下限',
              color: '#ec4899',
              fontSize: 12,
              fontWeight: 'bold'
            },
            lineStyle: {
              type: 'dashed',
              color: '#ec4899',
              width: 2
            },
            data: [
              {
                xAxis: LMinValue,
                name: '液相下限'
              }
            ],
            animation: false
          },
          z: 2
        },
        {
          name: '操作线',
          type: 'line',
          smooth: false,
          symbol: 'none',
          data: opLineData,
          lineStyle: {
            width: 4,
            type: 'dotted',
            color: '#60a5fa'
          },
          z: 4
        },
        {
          name: '操作点',
          type: 'scatter',
          symbolSize: 18,
          data: [[LOpValue, VOpValue]],
          itemStyle: {
            color: '#397429',
            borderColor: '#eee',
            borderWidth: 3,
          },
          label: {
            show: true,
            formatter: '操作点',
            position: 'top',
            color: '#000',
            fontWeight: 'bold',
            fontSize: 16,
            distance: 8
          },
          z: 5
        }
      ]
    };
  };

  return (
    <div className="hydraulics-chart-container">
      {/* ECharts容器 */}
      <div 
        ref={chartRef} 
        id="hydraulicsChart" 
        className="hydraulics-chart"
      ></div>

      {/* 加载中遮罩 */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <span className="loading-text">加载中...</span>
        </div>
      )}
    </div>
  );
};

export default HydraulicsChart;